/**
 * funChallengesSessionStore.ts
 *
 * Generic room/session mirror for the Fun Challenges feature. Kept SEPARATE
 * from the chess `GamePlayStore` so chess flows are completely untouched.
 *
 * Receives raw payloads from `useFunChallengesSocket` handlers; the higher-
 * level FSM lives in `funChallengesBattleStore`. Splitting the two keeps the
 * battle store focused on UI state while this one stays a faithful mirror of
 * what the server told us.
 */

import {create} from 'zustand';

import type {
  ChallengeSettings,
  FunRoomUpdatePayload,
} from '@/Screens/FunChallenges/types';

interface RoomState {
  roomId: string;
  creatorId: string;
  gameType: string;
  settings: ChallengeSettings;
  state: 'waiting' | 'in_session';
  players: FunRoomUpdatePayload['players'];
}

interface SessionState {
  sessionId: string;
  roomId: string;
  gameType: string;
  playerIds: string[];
  /** Server epoch ms when the timer fires. */
  endsAt: number;
  settings: ChallengeSettings;
  /** Full server-pre-rolled coord sequence; same for both players. */
  coords: string[];
}

interface FunChallengesSessionState {
  room: RoomState | null;
  session: SessionState | null;

  setRoom: (room: RoomState | null) => void;
  setRoomPlayers: (players: FunRoomUpdatePayload['players']) => void;
  setRoomState: (state: 'waiting' | 'in_session') => void;
  setSession: (session: SessionState | null) => void;
  clearAll: () => void;
}

const useFunChallengesSessionStore = create<FunChallengesSessionState>(set => ({
  room: null,
  session: null,

  setRoom: room => set({room}),
  setRoomPlayers: players =>
    set(prev => (prev.room ? {room: {...prev.room, players}} : prev)),
  setRoomState: state =>
    set(prev => (prev.room ? {room: {...prev.room, state}} : prev)),
  setSession: session => set({session}),
  clearAll: () => set({room: null, session: null}),
}));

export default useFunChallengesSessionStore;
export type {RoomState, SessionState};
